<template>
    <div class="calc-overlay" v-if="dataVal.flag" @click="handleOverlayClick">
        <div class="calc-card" ref="card" :style="cardStyle" @click.stop @mousedown="startDrag" @touchstart="startDrag">
            <div class="drag-area"></div>
            <header class="calc-header">
                <div class="header-tabs">
                    <button :class="['tab-btn', { active: activeTab === 'calc' }]" @click="activeTab = 'calc'">
                        计算器
                    </button>
                    <button :class="['tab-btn', { active: activeTab === 'note' }]" @click="activeTab = 'note'">
                        记事本
                    </button>
                </div>
                <button class="icon-btn" @click="handleRefund">✕</button>
            </header>

            <!-- 计算器面板 -->
            <div v-show="activeTab === 'calc'" class="calc-panel">
                <div class="calc-display">
                    <div class="history">{{ history || ' ' }}</div>
                    <div class="current">{{ display }}</div>
                </div>

                <div class="calc-grid">
                    <button v-for="btn in buttons" :title="btn.title" :key="btn.label" :class="['btn', btn.class]"
                        @click="handle(btn)">
                        {{ btn.label }}
                    </button>
                </div>

                <footer class="calc-footer">
                    <button class="primary" @click="reset">重置</button>
                </footer>
            </div>

            <!-- 记事本面板 -->
            <div v-show="activeTab === 'note'" class="note-panel">
                <div class="note-toolbar">
                    <button class="note-btn" @click="insertResult">插入计算结果</button>
                    <button class="note-btn" @click="clearNote">清空</button>
                    <button class="note-btn primary" @click="saveNote">保存</button>
                </div>
                <textarea v-model="noteContent" class="note-textarea" placeholder="在这里记录你的想法、计算结果或其他内容..."
                    @input="autoSave"></textarea>
                <div class="note-info">
                    <span>字数: {{ noteContent.length }}</span>
                    <span v-if="lastSaved">最后保存: {{ lastSaved }}</span>
                </div>
            </div>

            <div class="drag-area"></div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Calculator',
    props: ['dataVal'],
    data() {
        return {
            activeTab: 'calc',
            current: '0',
            history: '',
            lastOp: null,
            isDragging: false,
            dragStart: { x: 0, y: 0 },
            position: { x: 0, y: 0 },
            noteContent: '',
            lastSaved: '',
            autoSaveTimer: null,
            buttons: [
                { label: 'C', title: '清除', class: 'accent', action: 'clear' },
                { label: '⌫', title: '删除', class: 'accent', action: 'backspace' },
                { label: '%', title: '百分比', class: 'accent', action: 'percent' },
                { label: '÷', title: '除法', class: 'accent', action: '/' },
                { label: '√', title: '开平方', class: 'scientific', action: 'sqrt' },
                { label: 'sin', title: '正弦函数', class: 'scientific', action: 'sin' },
                { label: 'cos', title: '余弦函数', class: 'scientific', action: 'cos' },
                { label: 'tan', title: '正切函数', class: 'scientific', action: 'tan' },
                { label: '7', title: '7', action: '7' },
                { label: '8', title: '8', action: '8' },
                { label: '9', title: '9', action: '9' },
                { label: '×', title: '乘法', class: 'accent', action: '*' },
                { label: '4', title: '4', action: '4' },
                { label: '5', title: '5', action: '5' },
                { label: '6', title: '6', action: '6' },
                { label: '−', title: '减法', class: 'accent', action: '-' },
                { label: '1', title: '1', action: '1' },
                { label: '2', title: '2', action: '2' },
                { label: '3', title: '3', action: '3' },
                { label: '+', title: '加法', class: 'accent', action: '+' },
                { label: '0', title: '0', class: 'wide', action: '0' },
                { label: '.', title: '小数点', action: '.' },
                { label: '=', title: '等于', class: 'primary', action: 'equal' }
            ]
        }
    },
    computed: {
        display() {
            return this.current
        },
        cardStyle() {
            return {
                transform: `translate(${this.position.x}px, ${this.position.y}px)`
            }
        }
    },
    mounted() {
        window.addEventListener('keydown', this.handleEsc)
        window.addEventListener('mousemove', this.onDrag)
        window.addEventListener('mouseup', this.stopDrag)
        window.addEventListener('touchmove', this.onDrag, { passive: false })
        window.addEventListener('touchend', this.stopDrag)
        window.addEventListener('resize', this.constrainPosition)
        this.loadNote()
    },
    beforeDestroy() {
        window.removeEventListener('keydown', this.handleEsc)
        window.removeEventListener('mousemove', this.onDrag)
        window.removeEventListener('mouseup', this.stopDrag)
        window.removeEventListener('touchmove', this.onDrag)
        window.removeEventListener('touchend', this.stopDrag)
        window.removeEventListener('resize', this.constrainPosition)
        if (this.autoSaveTimer) {
            clearTimeout(this.autoSaveTimer)
        }
    },
    methods: {
        handleOverlayClick(e) {
            if (e.target === e.currentTarget) {
                this.handleRefund()
            }
        },
        handleRefund() {
            console.log('handleRefund')
            this.reset()
            let data = {
                flag: false
            }
            this.$emit("calculatorCall", data);
        },
        getBounds() {
            if (!this.$refs.card) {
                return { minX: 0, maxX: 0, minY: 0, maxY: 0 }
            }
            const rect = this.$refs.card.getBoundingClientRect()
            const cardWidth = rect.width
            const cardHeight = rect.height
            const windowWidth = window.innerWidth
            const windowHeight = window.innerHeight

            return {
                minX: -(windowWidth / 2 - cardWidth / 2),
                maxX: windowWidth / 2 - cardWidth / 2,
                minY: -(windowHeight / 2 - cardHeight / 2),
                maxY: windowHeight / 2 - cardHeight / 2
            }
        },
        constrainPosition() {
            const bounds = this.getBounds()
            this.position = {
                x: Math.max(bounds.minX, Math.min(bounds.maxX, this.position.x)),
                y: Math.max(bounds.minY, Math.min(bounds.maxY, this.position.y))
            }
        },
        startDrag(e) {
            const target = e.target
            const isDragArea = target.classList.contains('drag-area')
            const isCardEdge = this.isCardEdge(e)

            if (!isDragArea && !isCardEdge) {
                return
            }

            if (target.tagName === 'BUTTON' || target.closest('button')) {
                return
            }

            this.isDragging = true
            const clientX = e.touches ? e.touches[0].clientX : e.clientX
            const clientY = e.touches ? e.touches[0].clientY : e.clientY
            this.dragStart = {
                x: clientX - this.position.x,
                y: clientY - this.position.y
            }
        },
        isCardEdge(e) {
            if (!this.$refs.card) return false
            const rect = this.$refs.card.getBoundingClientRect()
            const edgeThreshold = 10
            const clientX = e.touches ? e.touches[0].clientX : e.clientX
            const clientY = e.touches ? e.touches[0].clientY : e.clientY

            const isLeftEdge = clientX >= rect.left && clientX <= rect.left + edgeThreshold
            const isRightEdge = clientX >= rect.right - edgeThreshold && clientX <= rect.right
            const isTopEdge = clientY >= rect.top && clientY <= rect.top + edgeThreshold
            const isBottomEdge = clientY >= rect.bottom - edgeThreshold && clientY <= rect.bottom

            return isLeftEdge || isRightEdge || isTopEdge || isBottomEdge
        },
        onDrag(e) {
            if (!this.isDragging) return
            e.preventDefault()
            const clientX = e.touches ? e.touches[0].clientX : e.clientX
            const clientY = e.touches ? e.touches[0].clientY : e.clientY
            let newX = clientX - this.dragStart.x
            let newY = clientY - this.dragStart.y

            const bounds = this.getBounds()
            newX = Math.max(bounds.minX, Math.min(bounds.maxX, newX))
            newY = Math.max(bounds.minY, Math.min(bounds.maxY, newY))

            this.position = {
                x: newX,
                y: newY
            }
        },
        stopDrag() {
            this.isDragging = false
        },
        backspace() {
            if (this.current.length > 1) {
                this.current = this.current.slice(0, -1)
            } else {
                this.current = '0'
            }
        },
        sqrt() {
            const num = Number(this.current)
            if (num < 0) {
                this.current = '错误'
                return
            }
            const result = Math.sqrt(num)
            this.current = String(Number.isFinite(result) ? +result.toFixed(10) : '错误')
        },
        sin() {
            const num = Number(this.current)
            const result = Math.sin(num * Math.PI / 180)
            this.current = String(Number.isFinite(result) ? +result.toFixed(10) : '错误')
        },
        cos() {
            const num = Number(this.current)
            const result = Math.cos(num * Math.PI / 180)
            this.current = String(Number.isFinite(result) ? +result.toFixed(10) : '错误')
        },
        tan() {
            const num = Number(this.current)
            const result = Math.tan(num * Math.PI / 180)
            this.current = String(Number.isFinite(result) ? +result.toFixed(10) : '错误')
        },
        appendDigit(d) {
            if (this.current === '0' && d !== '.') {
                this.current = d
            } else if (d === '.' && this.current.includes('.')) {
                return
            } else {
                this.current += d
            }
        },
        setOp(op) {
            if (this.lastOp) {
                this.evaluate()
            }
            this.history = this.current + ' ' + op
            this.lastOp = op
            this.current = '0'
        },
        evaluate() {
            if (!this.lastOp || this.history === '') {
                return
            }
            try {
                const expr = this.history.replace('÷', '/').replace('×', '*').replace('−', '-')
                const result = eval(expr + this.current) // eslint-disable-line no-eval
                this.current = String(Number.isFinite(result) ? +result.toFixed(10) : '错误')
                this.history = ''
                this.lastOp = null
            } catch {
                this.current = '错误'
                this.history = ''
                this.lastOp = null
            }
        },
        reset() {
            this.current = '0'
            this.history = ''
            this.lastOp = null
        },
        handle(btn) {
            const a = btn.action
            if (a === 'clear') {
                return this.reset()
            }
            if (a === 'backspace') {
                return this.backspace()
            }
            if (a === 'sqrt') {
                return this.sqrt()
            }
            if (a === 'sin') {
                return this.sin()
            }
            if (a === 'cos') {
                return this.cos()
            }
            if (a === 'tan') {
                return this.tan()
            }
            if (a === 'sign') {
                this.current = String(-Number(this.current))
                return
            }
            if (a === 'percent') {
                this.current = String(Number(this.current) / 100)
                return
            }
            if (a === 'equal') {
                return this.evaluate()
            }
            if (['+', '-', '*', '/'].includes(a)) {
                return this.setOp(a)
            }
            return this.appendDigit(a)
        },
        handleEsc(e) {
            if (e.key === 'Escape') {
                this.handleRefund()
            }
        },
        // 记事本相关方法
        insertResult() {
            const result = this.current !== '错误' ? this.current : ''
            if (result) {
                const cursorPos = this.$refs.noteTextarea ? this.$refs.noteTextarea.selectionStart : this.noteContent.length
                this.noteContent =
                    this.noteContent.slice(0, cursorPos) +
                    result +
                    this.noteContent.slice(cursorPos)
            }
        },
        clearNote() {
            if (confirm('确定要清空记事本内容吗？')) {
                this.noteContent = ''
                this.saveNote()
            }
        },
        saveNote() {
            try {
                localStorage.setItem('calculator_note', this.noteContent)
                const now = new Date()
                this.lastSaved = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
            } catch (e) {
                console.error('保存失败:', e)
            }
        },
        loadNote() {
            try {
                const saved = localStorage.getItem('calculator_note')
                if (saved) {
                    this.noteContent = saved
                }
                const savedTime = localStorage.getItem('calculator_note_time')
                if (savedTime) {
                    this.lastSaved = savedTime
                }
            } catch (e) {
                console.error('加载失败:', e)
            }
        },
        autoSave() {
            if (this.autoSaveTimer) {
                clearTimeout(this.autoSaveTimer)
            }
            this.autoSaveTimer = setTimeout(() => {
                this.saveNote()
            }, 2000) // 2秒后自动保存
        }
    }
}
</script>

<style scoped>
.calc-overlay {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
    z-index: 9999;
    background: rgba(0, 0, 0, 0.3);
}

.calc-card {
    pointer-events: auto;
    width: min(92vw, 420px);
    background: linear-gradient(145deg, #1f1f24, #24242a);
    color: #f8f9fb;
    border-radius: 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.06);
    backdrop-filter: blur(10px);
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    position: relative;
    user-select: none;
    transition: transform 0s;
    max-height: 90vh;
}

.drag-area {
    position: absolute;
    left: 0;
    right: 0;
    height: 10px;
    cursor: move;
    z-index: 1;
}

.drag-area:first-child {
    top: 0;
    border-radius: 20px 20px 0 0;
}

.drag-area:last-child {
    bottom: 0;
    border-radius: 0 0 20px 20px;
}

.calc-card::before,
.calc-card::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    width: 10px;
    height: 100%;
    cursor: move;
    z-index: 1;
}

.calc-card::before {
    left: 0;
    border-radius: 20px 0 0 20px;
}

.calc-card::after {
    right: 0;
    left: auto;
    border-radius: 0 20px 20px 0;
}

.calc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 600;
    letter-spacing: 0.3px;
    position: relative;
    z-index: 2;
    font-size: 14px;
}

.header-tabs {
    display: flex;
    gap: 8px;
}

.tab-btn {
    background: rgba(255, 255, 255, 0.06);
    border: none;
    color: #b8bdc7;
    padding: 6px 12px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    transition: all 0.2s;
}

.tab-btn:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
}

.tab-btn.active {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: #fff;
}

.icon-btn {
    background: transparent;
    border: none;
    color: #b8bdc7;
    font-size: 18px;
    cursor: pointer;
    padding: 6px;
    border-radius: 10px;
    transition: background 0.2s, color 0.2s;
    position: relative;
    z-index: 2;
}

.icon-btn:hover {
    background: rgba(255, 255, 255, 0.06);
    color: #fff;
}

.calc-panel {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.calc-display {
    background: rgba(255, 255, 255, 0.04);
    border-radius: 16px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-height: 84px;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.05);
    position: relative;
    z-index: 2;
}

.history {
    color: #8a90a0;
    font-size: 13px;
    text-align: right;
    min-height: 16px;
}

.current {
    font-size: clamp(28px, 4vw, 36px);
    text-align: right;
    font-weight: 700;
    word-break: break-all;
}

.calc-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    position: relative;
    z-index: 2;
}

.btn {
    border: none;
    border-radius: 14px;
    padding: 12px 0;
    font-size: 16px;
    font-weight: 600;
    background: rgba(255, 255, 255, 0.06);
    color: #eef1f6;
    cursor: pointer;
    transition: transform 0.08s ease, background 0.2s, box-shadow 0.2s;
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.25);
    position: relative;
    z-index: 2;
}

.btn:active {
    transform: translateY(1px) scale(0.99);
}

.btn:hover {
    background: rgba(255, 255, 255, 0.1);
}

.btn.accent {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.btn.scientific {
    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
    font-size: 14px;
}

.btn.primary {
    background: linear-gradient(135deg, #06b6d4, #0ea5e9);
}

.btn.wide {
    grid-column: span 2;
}

.calc-footer {
    display: flex;
    justify-content: center;
    gap: 10px;
    position: relative;
    z-index: 2;
}

.calc-footer .primary {
    flex: 1;
    padding: 12px 0;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    cursor: pointer;
    font-weight: 600;
    background: linear-gradient(135deg, #22c55e, #16a34a);
    color: #fff;
}

/* 记事本样式 */
.note-panel {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 400px;
    max-height: 60vh;
}

.note-toolbar {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.note-btn {
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.08);
    color: #eef1f6;
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    transition: all 0.2s;
}

.note-btn:hover {
    background: rgba(255, 255, 255, 0.1);
}

.note-btn.primary {
    background: linear-gradient(135deg, #22c55e, #16a34a);
    border-color: transparent;
    color: #fff;
}

.note-textarea {
    flex: 1;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    padding: 12px;
    color: #f8f9fb;
    font-size: 14px;
    font-family: inherit;
    resize: none;
    min-height: 300px;
    outline: none;
    user-select: text;
}

.note-textarea::placeholder {
    color: #8a90a0;
}

.note-textarea:focus {
    border-color: rgba(59, 130, 246, 0.5);
    background: rgba(255, 255, 255, 0.06);
}

.note-info {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #8a90a0;
    padding: 0 4px;
}

@media (max-width: 480px) {
    .calc-card {
        width: 96vw;
        padding: 14px;
        max-height: 95vh;
    }

    .btn {
        padding: 10px 0;
        font-size: 14px;
    }

    .btn.scientific {
        font-size: 12px;
    }

    .calc-header {
        font-size: 12px;
    }

    .note-panel {
        min-height: 300px;
        max-height: 50vh;
    }

    .note-textarea {
        min-height: 250px;
        font-size: 13px;
    }

    .note-btn {
        padding: 6px 12px;
        font-size: 12px;
    }
}
</style>